import utils from '@eitje/utils'
import _ from 'lodash'
import React, {useEffect, useRef, useState} from 'react'
import {useFormSelector} from './context'
import {Tooltip, config} from './base'
import {findFns} from './field_click_helper'
import {LeftContent} from './left_content'
import {RightContent} from './right_content'
import {makeField} from './actions'
import {makeNewRegisteredField} from './use_register_field'
import {makeRegisteredField} from './use_legacy_register_field'

export const decorateField =
	(Comp, compOpts = {}) =>
	props => {
		const opts = utils.funcOrVal(compOpts, props)
		const containerClassName = utils.funcOrVal(opts.className, props)

		const element = useRef(null)
		const [fieldOpen, setOpen] = useState(false)
		const contextFormName = useFormSelector('formName')
		const inputRef = useRef()

		const onOpenChange = open => {
			// Our Ark UI picker components (e.g. DatePicker) enables `closeOnClickOutside`, but at the same time we want to support
			// clicking the field to close an opened picker. Therefore, we add a delay to the native change listener such that our own form click logic
			// can run first (field_click_helper.js)
			setTimeout(() => {
				setOpen(open)
			}, 200)
		}

		const allProps = {
			innerRef: inputRef,
			..._.omit(opts, 'className'), // className is meant for container
			...props,
			formName: props.formName || contextFormName,
			Comp,
			onOpenChange,
			onVisibleChange: onOpenChange,
		}
		const {readOnly, isFirst, autoFocus = true, className, error, disabled} = allProps

		const clickChild = event => {
			const classNames = containerClassName?.split(' ') || []
			const fn = findFns[classNames.find(name => findFns[name])] || findFns['default']

			if (readOnly || disabled) return null

			fn(element.current, {event, open: fieldOpen})
		}

		const classNames = utils.makeCns(
			`eitje-form-3-field`,
			disabled && 'disabled',
			error && 'error',
			readOnly && 'read-only',
			className,
			containerClassName,
		)

		// Extract relevant props for html only
		const {
			onChange,
			items,
			innerRef,
			i18nOpts,
			field,
			groupField,
			defaultSubmitStrategy,
			tableName,
			value,
			formName,
			borderTop,
			textProps,
			tooltip,
			...htmlProps
		} = allProps

		useEffect(() => {
			if (isFirst && autoFocus) {
				element.current?.querySelector('input')?.focus?.()
			}
		}, [isFirst, autoFocus])

		return (
			<div {...htmlProps} ref={element} onClick={clickChild} className={classNames} data-state={fieldOpen ? 'open' : 'closed'}>
				<TooltipWrapper tooltip={tooltip}>
					<config.Layout
						className="form-field-content"
						horizontal="spaceBetween"
						height="full"
						padding="16 24"
						disabled={disabled}
						borderTop={borderTop}
					>
						<LeftContent {...allProps} />
						<RightContent {...allProps} />
					</config.Layout>
				</TooltipWrapper>
			</div>
		)
	}

const TooltipWrapper = ({tooltip, children}) => {
	if (!tooltip) return children
	return <Tooltip title={tooltip}>{children}</Tooltip>
}

export const makeNewField = (Comp, compOpts = {}) => {
	const Formified = decorateField(Comp, compOpts)
	return makeNewRegisteredField(Formified, compOpts)
}

const _buildField = components => props => {
	const {Full, Form, Base, NewWithoutDecoration, New} = components
	const {form = true, decorated = true, raw} = props
	const isNew = useFormSelector('newForm')
	if (isNew) {
		if (decorated) return <New {...props} newForm />
		return <NewWithoutDecoration {...props} newForm />
	}
	if (raw) return <Base {...props} />
	if (form && decorated) return <Full {...props} />
	if (form) return <Form {...props} />
	return <Base {...props} />
}

export const buildField = (Base, opts = {}) => {
	return _buildField({
		Full: makeField(Base, opts),
		Form: makeRegisteredField(Base),
		New: makeNewField(Base, opts),
		NewWithoutDecoration: makeNewRegisteredField(Base),
		Base,
	})
}
